import {
  getResourceConsumptionGroupRangeKey
} from './resource-consumption-base.constant';
import { FEATURE_NAME } from './resource-consumption-base.constant';
import {
  ResourceConsumptionGroupRequestPayload,
  ResourceConsumptionGroupUpdateSuccessPayload,
  ResourceConsumptionGroupSearchRequest
} from './resource-consumption-base.model';
import {
  createTimeSeriesSubscriptionName,
  timeSeriesGroupExists
} from '../shared';
import { endOfDay, endOfMonth, startOfMonth, endOfHour } from 'date-fns';

// Time Period Settings
function consumptionPeriod(key: string, fromDate: Date) {
  switch (key) {
    case 'today':
    case 'yesterday':
    case 'last24hours':
      return {
        from: fromDate,
        to: endOfHour(fromDate),
      };
    case 'last7days':
    case 'last30days':
    case 'thisMonth':
    case 'lastMonth':
      return {
        from: fromDate,
        to: endOfDay(fromDate),
      };
    case 'last12month':
      return {
        from: startOfMonth(fromDate),
        to: endOfMonth(fromDate),
      };
    default:
      return {
        from: fromDate,
        to: fromDate
      };
  }
}


export function getResourceConsumptionGroupSubscriptionName(
  groupBy: string,
  timeGroupBy: string,
  metricId?: string,
  projectId?: string,
  key?: string
): string {
  return createTimeSeriesSubscriptionName(
    FEATURE_NAME,
    groupBy,
    timeGroupBy,
    key,
    projectId,
    undefined, // serviceId
    metricId
  );
}

export function resourceConsumptionGroupExists(
  payload: ResourceConsumptionGroupRequestPayload,
  resourceConsumptionStateData: any
): boolean {
  return timeSeriesGroupExists(
    payload,
    resourceConsumptionStateData,
    getResourceConsumptionGroupRangeKey,
    (p) => p.groupBy,
    (p) => p.groupRange,
    (p) => p.projectId,
    undefined // no serviceId for resource consumption
  );
}

export function transformResourceConsumptionGroupData(
  data: ResourceConsumptionGroupUpdateSuccessPayload,
  state: any
): any {
  const { items, projectId, key } = data;

  if (!projectId) {
    console.warn('Resource consumption data missing projectId');
    return state;
  }

  const rangeKey = getResourceConsumptionGroupRangeKey(key);

  // Transform items into chart-ready format, grouped by metricId like transaction-debit
  const transformedData = transformResourceConsumptionItems(items || [], rangeKey);

  return {
    ...state,
    [projectId]: {
      ...(state[projectId] || {}),
      [rangeKey]: transformedData
    }
  };
}

function transformResourceConsumptionItems(items: any[], rangeKey: string) {
  if (!items?.length) {
    return { data: [] };
  }

  // Group items by time (from) and aggregate metrics
  const timeGroups = items.reduce((acc, item) => {
    const timeKey = item.from;

    if (!acc[timeKey]) {
      const fromDate = new Date(item.from);

      acc[timeKey] = {
        x: new Date(item.from),
        range: consumptionPeriod(rangeKey, fromDate),
        nameMap: {},
        _keys: [],
        total: 0
      };
    }

    // Add metric to this time point
    acc[timeKey][item.metricId] = item.amount;
    acc[timeKey].nameMap[item.metricId] = getMetricDisplayName(item.metricId);
    acc[timeKey]._keys.push(item.metricId);
    acc[timeKey].total += item.amount;

    return acc;
  }, {});

  // Convert to array and sort by time
  const chartData = Object.values(timeGroups).sort((a: any, b: any) =>
    a.x.getTime() - b.x.getTime()
  );

  return {
    data: chartData
  };
}

function getMetricDisplayName(metricId: string): string {
  const metricNames = {
    'MASTER_EGRESS': 'Egress',
    'BUILD_TIME': 'Build Time',
    'BACKUP_STORAGE': 'Backup storage'
  };

  return metricNames[metricId] || metricId;
}

export function buildResourceConsumptionGroupSearchRequest(
  payload: ResourceConsumptionGroupRequestPayload,
  clientId: string,
  receiverId?: string
): ResourceConsumptionGroupSearchRequest {
  // Custom request builder for resource consumption - metricId is not sent as a search filter
  const search: any[] = [
    { name: 'clientId', operator: 'eq', value: clientId }
  ];

  if (payload.projectId) {
    search.push({ name: 'projectId', operator: 'eq', value: payload.projectId });
  }

  const subscriptionName = getResourceConsumptionGroupSubscriptionName(
    payload.groupBy,
    payload.groupRange.timeGroupBy,
    payload.metricId,
    payload.projectId,
    payload.groupRange.key
  );

  return {
    search,
    receiverId,
    subscriptionName,
    groupBy: payload.groupBy,
    timeGroupBy: payload.groupRange.timeGroupBy,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    limit: payload.groupRange.limit,
    from: payload.groupRange.range?.from,
    till: payload.groupRange.range?.to,
    // Include metricId as a separate field, not in search filters
    metricId: payload.metricId
  };
}
